// MCHD Room Scheduler - Blocks React Query Hooks
// React Query hooks for block management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../useAuth';
import {
  getBlocks,
  getBlockById,
  createBlock,
  updateBlock,
  deleteBlock,
  getBlockStats,
  getBlocksForSelection,
  GetBlocksParams,
  CreateBlockData,
  UpdateBlockData,
  GetBlockStatsParams,
} from '../../api/endpoints/blocks';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const blockQueryKeys = {
  all: ['blocks'] as const,
  lists: () => [...blockQueryKeys.all, 'list'] as const,
  list: (params: GetBlocksParams) => [...blockQueryKeys.lists(), params] as const,
  details: () => [...blockQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...blockQueryKeys.details(), id] as const,
  stats: (params?: GetBlockStatsParams) => [...blockQueryKeys.all, 'stats', params] as const,
  selection: () => [...blockQueryKeys.all, 'selection'] as const,
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to get all blocks with filtering and pagination
 * @param params - Query parameters
 * @returns Blocks query result
 */
export function useBlocks(params: GetBlocksParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: blockQueryKeys.list(params),
    queryFn: () => getBlocks(params, token!),
    enabled: !!token,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get block by ID
 * @param id - Block ID
 * @param options - Query options
 * @returns Block query result
 */
export function useBlockById(id: string, options: { enabled?: boolean } = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: blockQueryKeys.detail(id),
    queryFn: () => getBlockById(id, token!),
    enabled: !!token && !!id && (options.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get block statistics
 * @param params - Query parameters
 * @returns Block statistics query result
 */
export function useBlockStats(params: GetBlockStatsParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: blockQueryKeys.stats(params),
    queryFn: () => getBlockStats(params, token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get blocks for selection (dropdown)
 * @returns Blocks selection query result
 */
export function useBlocksForSelection() {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: blockQueryKeys.selection(),
    queryFn: () => getBlocksForSelection(token!),
    enabled: !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new block
 * @returns Create block mutation
 */
export function useCreateBlock() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useMutation({
    mutationFn: (data: CreateBlockData) => createBlock(data, token!),
    onSuccess: () => {
      // Invalidate and refetch blocks queries
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.stats() });
    },
    onError: (error: any) => {
      console.error('Error creating block:', error);
    },
  });
}

/**
 * Hook to update an existing block
 * @returns Update block mutation
 */
export function useUpdateBlock() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBlockData }) => updateBlock(id, data, token!),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch blocks queries
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.stats() });
    },
    onError: (error: any) => {
      console.error('Error updating block:', error);
    },
  });
}

/**
 * Hook to delete a block
 * @returns Delete block mutation
 */
export function useDeleteBlock() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useMutation({
    mutationFn: (id: string) => deleteBlock(id, token!),
    onSuccess: () => {
      // Invalidate and refetch blocks queries
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: blockQueryKeys.stats() });
    },
    onError: (error: any) => {
      console.error('Error deleting block:', error);
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to get blocks for selection with specific filters
 * @param filters - Additional filters
 * @returns Blocks for selection query result
 */
export function useBlocksForSelectionWithFilters(filters: { isActive?: boolean } = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: [...blockQueryKeys.selection(), filters],
    queryFn: () => getBlocksForSelection(token!),
    enabled: !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    select: (data) => {
      if (!data?.data?.blocks) return data;
      
      let filteredBlocks = data.data.blocks;
      
      if (filters.isActive !== undefined) {
        // Note: This is a client-side filter since the API doesn't support it
        // In a real implementation, you'd want to add this to the API
        filteredBlocks = filteredBlocks.filter(block => {
          const now = new Date();
          const start = new Date(block.startDate);
          const end = new Date(block.endDate);
          const isCurrentlyActive = now >= start && now <= end;
          return filters.isActive ? isCurrentlyActive : !isCurrentlyActive;
        });
      }
      
      return {
        ...data,
        data: {
          ...data.data,
          blocks: filteredBlocks,
        },
      };
    },
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get upcoming blocks
 * @returns Upcoming blocks query result
 */
export function useUpcomingBlocks() {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: [...blockQueryKeys.all, 'upcoming'],
    queryFn: () => getBlocks({ 
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      sortBy: 'startDate',
      sortOrder: 'asc',
    }, token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get current blocks (active and within date range)
 * @returns Current blocks query result
 */
export function useCurrentBlocks() {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: [...blockQueryKeys.all, 'current'],
    queryFn: () => getBlocks({ 
      isActive: true,
      sortBy: 'startDate',
      sortOrder: 'asc',
    }, token!),
    enabled: !!token,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => {
      if (!data?.data?.blocks) return data;
      
      const now = new Date();
      const currentBlocks = data.data.blocks.filter(block => {
        const start = new Date(block.startDate);
        const end = new Date(block.endDate);
        return now >= start && now <= end;
      });
      
      return {
        ...data,
        data: {
          ...data.data,
          blocks: currentBlocks,
        },
      };
    },
    retry: (failureCount, error: any) => {
      // Don't retry on authentication errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
